import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { usePlayerProfileStore } from '~/stores/player-profile';

/**
 * Status VIP do usuário logado, lido do `vip` de `GET /players/me` (store
 * `player-profile`).
 *
 * De onde vem o dado: o core devolve `vip: null | 'VIP 3'` na mesma resposta que
 * responde a elegibilidade das promoções — uma chamada pras duas perguntas. Quem
 * dispara e invalida é `plugins/player-profile.client.ts`.
 *
 * ANTES vinha das `core_public_tags` do perfil da Smartico, e o tier era extraído
 * do TEXTO da tag por regex. Saiu de lá por dois motivos: dependia de uma cadeia
 * inteira antes de existir (SDK baixado → `identify` concluído → perfil mergeado
 * pelos listeners), que é o que fazia a janela de "todo mundo parece não-VIP" ser
 * longa e imprevisível; e o formato da tag não é contrato versionado por ninguém —
 * o core escreve 'VIP 3' com espaço, e o parser esperava `VIP_3`.
 *
 * O primeiro motivo continua valendo (o dado vem do /players/me, não do SDK); o
 * segundo voltou a ser um risco em 19/08/2026, quando o core achatou o `vip` em
 * string e parou de mandar o `order`. A leitura do texto está isolada num lugar
 * só (`vipOrderFromTag`) e é folgada de propósito, mas se a tag mudar de forma
 * de novo é lá que quebra — e o sintoma é VIP virar jogador comum.
 *
 * O que se perdeu: a Smartico re-mergeava o perfil no `props_change`, então
 * mudança de nível refletia sem reload. Aqui o nível é o que veio na primeira
 * resposta DA ABA (o memo do handler corta as seguintes) — aba nova traz
 * atualizado. Decisão de produto: nível muda pouco.
 *
 * O que NÃO fazer: tratar `isVip === false` como "não é VIP". Até a resposta
 * chegar, todo mundo parece não-VIP. Quem precisa distinguir "não é" de "ainda
 * não sei" usa `isVipReady`. Pra só ENFEITAR (chip, selo), `isVip` basta: aparece
 * quando o dado chega.
 */
export function useVipStatus() {
  const { vip, isVip, isProfileKnown } = storeToRefs(usePlayerProfileStore());

  // A resposta chegou (com nível ou sem) — é o sinal de "já sei a resposta".
  const isVipReady = isProfileKnown;

  // O nível, direto do `order` do core: 0..6 nos VIP numerados e 7 no SIGNATURE.
  // É o que escolhe a versão do card — quem não tem arte pro número cai na arte
  // padrão (ver `vipTierStyle`), e é assim que o SIGNATURE se comporta hoje.
  //
  // CUIDADO: o tier 0 existe e é falsy. Testar sempre `vipTier !== null`, nunca
  // `if (vipTier)`, senão o VIP 0 é tratado como "sem tier" e perde a arte dele.
  const vipTier = computed(() => vip.value?.order ?? null);

  // WhatsApp do gerente dedicado (só o core preenche a partir do tier
  // gerente — ver MANAGER_TIER em components/user-v2/Header.vue, que decide
  // QUANDO mostrar o botão; aqui é só o dado cru, `null` se não vier).
  const vipManagerWhatsapp = computed(() => vip.value?.supportWhatsapp ?? null);

  return { isVip, isVipReady, vipTier, vipManagerWhatsapp };
}
